import { createWlinkI18n } from "@wlink/commons/i18n";
import { ElMessageBox } from "element-plus";
const i18n = createWlinkI18n();
const { t } = i18n.global;
/**
 * 中国地区限制 composable
 * 用于统一处理中国地区选择的限制逻辑
 *
 * ## 功能说明
 *
 * 提供统一的中国地区限制提示，主要用于处理以下场景：
 * - 国家选择时限制中国大陆地区
 * - 牌照设置时限制中国大陆地区
 * - 其他需要限制中国地区的场景
 *
 * ## 基本用法
 *
 * ### 1. 单选国家限制
 * ```typescript
 * import { useChinaRestriction } from '@wlink/commons/core'
 *
 * const { handleCountryChange } = useChinaRestriction()
 *
 * const onCountryChange = async (value: string) => {
 *   const result = await handleCountryChange(value, (newValue) => {
 *     // 更新表单数据的回调函数
 *     formData.value.country = newValue
 *   })
 * }
 * ```
 *
 * ### 2. 多选国家限制
 * ```typescript
 * import { useChinaRestriction } from '@wlink/commons/core'
 *
 * const { handleCountriesChange } = useChinaRestriction()
 *
 * const onCountriesChange = async (value: string[]) => {
 *   const result = await handleCountriesChange(value, (newValue) => {
 *     // 更新表单数据的回调函数
 *     formData.value.countries = newValue
 *   })
 * }
 * ```
 *
 * ### 3. 自定义提示信息
 * ```typescript
 * const { showChinaRestrictionAlert } = useChinaRestriction()
 *
 * const customAlert = async () => {
 *   await showChinaRestrictionAlert({
 *     message: '自定义提示信息',
 *     confirmButtonText: '我知道了'
 *   })
 * }
 * ```
 *
 * ## API 参考
 *
 * ### ChinaRestrictionOptions
 * | 属性 | 类型 | 默认值 | 说明 |
 * |-----|------|--------|------|
 * | message | string | "尊敬的用户：\n当前产品服务不支持中国大陆地区。" | 提示信息 |
 * | title | string | "提示" | 弹窗标题 |
 * | confirmButtonText | string | "我已知晓" | 确认按钮文字 |
 * | type | "info" \| "success" \| "warning" \| "error" | "warning" | 弹窗类型 |
 *

 * ## 注意事项
 *
 * 1. 弹窗基于 Element Plus 的 MessageBox 组件实现
 * 2. 无论用户确认还是取消，都会执行相应的限制逻辑
 * 3. 单选模式下会清空选择，多选模式下会移除中国选项
 * 4. 建议在组件中直接使用提供的处理函数
 *
 * @author Generated by AI Assistant
 * @since 1.0.0
 */

export interface ChinaRestrictionOptions {
  message?: string;
  title?: string;
  confirmButtonText?: string;
  type?: "info" | "success" | "warning" | "error";
}

/**
 * 中国地区限制 composable
 * @returns 返回处理中国地区限制的相关函数
 */
export const useChinaRestriction = () => {
  /**
   * 显示中国地区限制提示弹窗
   * @param options 弹窗配置选项
   */
  const showChinaRestrictionAlert = async (
    options?: ChinaRestrictionOptions
  ): Promise<void> => {
    const defaultOptions: ChinaRestrictionOptions = {
      message: t("72082") + "\n" + t("72083"),
      title: t("60985"),
      confirmButtonText: t("72084"),
      type: "warning",
    };

    const finalOptions = { ...defaultOptions, ...options };

    try {
      await ElMessageBox.alert(finalOptions.message!, finalOptions.title!, {
        confirmButtonText: finalOptions.confirmButtonText!,
        type: finalOptions.type!,
        dangerouslyUseHTMLString: false,
      });
    } catch (error) {
      // 用户取消或关闭弹窗，这里不需要特殊处理
    }
  };

  /**
   * 处理单选国家选择变化
   * @param value 当前选择的国家值
   * @param updateCallback 更新表单数据的回调函数
   */
  const handleCountryChange = async (
    value: string,
    updateCallback: (newValue: string) => void
  ): Promise<void> => {
    if (value === "CN") {
      await showChinaRestrictionAlert();
      // 清空选择
      updateCallback("");
    }
  };

  /**
   * 处理多选国家选择变化
   * @param value 当前选择的国家值数组
   * @param updateCallback 更新表单数据的回调函数
   */
  const handleCountriesChange = async (
    value: string[],
    updateCallback: (newValue: string[]) => void
  ): Promise<void> => {
    if (value.includes("CN")) {
      await showChinaRestrictionAlert();
      // 从数组中移除中国
      const filteredValue = value.filter((country) => country !== "CN");
      updateCallback(filteredValue);
    }
  };

  /**
   * 检查区号限制，并提示弹窗
   * @param phoneCode 形如 +86 的区号
   * @returns 返回 Promise<boolean>，true 表示受限（要拦截），false 表示通过
   */
  const checkPhoneCode = async (phoneCode: string): Promise<boolean> => {
    // +86 及变体都提示
    if (phoneCode === "+86" || phoneCode === "+086") {
      await showChinaRestrictionAlert();
      return true;
    }
    return false;
  };

  return {
    showChinaRestrictionAlert,
    handleCountryChange,
    handleCountriesChange,
    checkPhoneCode,
  };
};
